import { EventEmitter } from 'eventemitter3';
import { addListener, cacheObj, getCacheObj, setProps } from '../misc';
import { ConnectOption, WalletHub, WalletMap } from './WalletHub';
import { onlyCallOnceInOneTick, sleep } from '@web3/utils';
import { debugLog } from '../../utils/common';

type WalletHubCenterEvents = {
  walletChange: [WalletMap | undefined];
  walletHubChange: [WalletHub | undefined, WalletHub | undefined];
  onAddWalletHub: [WalletHub];
  onRemoveWalletHub: [WalletHub];
  switchSuccess: [WalletHub, boolean];
  recoveryFailed: [WalletHub, unknown];
  connectFailed: [WalletHub, unknown];
  switchingChange: [boolean, string];
};

export type WalletHubCenterOption = {
  getWalletHubs: () => Promise<WalletHub[]>;
  // 是否自动尝试恢复所有的hub的钱包信息（即能连上的都会进行连接，效果就是可以在界面上显示出所有已经连接的钱包的地址）
  autoRecoveryAll?: boolean;
  activeWalletHubStorageKey?: string;
  initDelay?: number;
  // 获取初始的initial hub
  getInitialWalletHub?: (storedWalletHubName?: string) => Promise<string | undefined>;
};

export class WalletHubCenter extends EventEmitter<WalletHubCenterEvents> {
  option: WalletHubCenterOption;
  walletHubs: WalletHub[] = [];

  constructor(option: WalletHubCenterOption) {
    super();
    this.option = setProps<WalletHubCenterOption>(
      {
        activeWalletHubStorageKey: 'web3-current-wallet',
        autoRecoveryAll: false,
        getWalletHubs: async () => {
          throw new Error('getWalletHubs is required');
        },
      },
      option,
    );
    this.ready = this.option.initDelay ? sleep(this.option.initDelay).then(() => this._init()) : this._init();
  }

  public ready: Promise<void>;

  private _activeHubId?: string;

  // 当前活动的钱包名称
  get activeHubId() {
    return this._activeHubId;
  }

  private set activeHubId(name: string | undefined) {
    this._activeHubId = name;
    cacheObj(name, this.option.activeWalletHubStorageKey);
  }

  private _findWalletHubIndex(hub: WalletHub | string): number {
    return this.walletHubs.findIndex((w) => (typeof hub === 'string' ? w.id === hub : w === hub));
  }

  // 是否有某个钱包
  hasWalletHub(hub: WalletHub | string): boolean {
    return this._findWalletHubIndex(hub) > -1;
  }

  // 添加一个钱包
  addWalletHub(hub: WalletHub) {
    if (this.hasWalletHub(hub)) {
      return;
    }
    this.walletHubs.push(hub);
    this.emit('onAddWalletHub', hub);
    this.ready.then(() => this._recoveryInitial());
  }

  // 移除一个钱包
  removeWalletHub(hub: WalletHub | string): WalletHub | undefined {
    const index = this._findWalletHubIndex(hub);
    if (index > -1) {
      const hubLocal = this.walletHubs[index];
      hubLocal.disconnect();
      this.walletHubs.splice(index, 1);
      this.emit('onRemoveWalletHub', hubLocal);
      return hubLocal;
    }
    return;
  }

  // 获取钱包hub
  getWalletHub(hub: WalletHub | string): WalletHub | undefined {
    return this.walletHubs[this._findWalletHubIndex(hub)];
  }

  getWalletHubsByTag(tag: string) {
    return this.walletHubs.filter((w) => w.tags.includes(tag));
  }

  // 当前激活的钱包
  get activeWalletHub() {
    if (!this.activeHubId) {
      return;
    }
    return this.walletHubs[this._findWalletHubIndex(this.activeHubId)];
  }

  // 钱包清单
  get wallets() {
    return this.activeWalletHub?.wallets;
  }

  get isEmpty() {
    return !this.hasAddress;
  }

  get hasAddress() {
    return Boolean(this.wallets && Object.keys(this.wallets).length);
  }

  private _disposeHub?: () => void;

  private _switchingHub?: string;

  private _setSwitching(h?: string) {
    this._switchingHub = h;
    this.emit('switchingChange', this.isSwitching, this._switchingHub ?? '');
  }

  public get isSwitching() {
    return Boolean(this._switchingHub);
  }

  /**
   * 切换钱包
   * @param hubId 钱包名称
   * @param recovery 是否是恢复模式
   * @returns
   */
  async switch(hubId?: string, recovery = false, opt?: ConnectOption & { timeout?: number }) {
    if (!hubId) {
      return;
    }
    const walletHub = this.getWalletHub(hubId);
    if (!walletHub) {
      return;
    }
    if (this._switchingHub) {
      throw new Error(`busy for switching to ${this._switchingHub}`);
    }
    this._setSwitching(walletHub.name);
    const oldWalletHub = this.activeWalletHub;

    try {
      const _promise = Promise.race([
        walletHub.connect(recovery, opt),
        sleep(opt?.timeout || recovery ? 15e3 : 30e3).then(() =>
          Promise.reject(new Error(`${recovery ? 'recovery' : 'connect'} wallet timeout: ${walletHub.name}`)),
        ),
      ]);

      await _promise.finally(() => {
        this._setSwitching();
      });

      const wallets = walletHub.wallets;

      if (!Object.keys(wallets).length) {
        throw new Error(`wallet hub connect fail: ${walletHub.id}:${walletHub.name}`);
      }
      // 切换钱包的情况下，就只是取消之前钱包的订阅信息，不关注它的变化，切换回来后可以直接使用
      this._disposeHub?.();
      this._disposeHub = undefined;

      this._disposeHub = this._listenHub(walletHub);
      this.activeHubId = hubId;
      this.emit('walletHubChange', walletHub, oldWalletHub);
      this.emit('walletChange', wallets);
      this.emit('switchSuccess', walletHub, recovery);
      return wallets;
    } catch (err) {
      console.error(err);
      this.emit(recovery ? 'recoveryFailed' : 'connectFailed', walletHub, err);
      throw err;
    }
  }

  public dispose(disconnect = true) {
    this._disposeHub?.();
    const activeWalletHub = this.activeWalletHub;
    if (activeWalletHub) {
      if (disconnect) {
        this.activeHubId = undefined;
      } else {
        // 仅仅去掉状态，下次可恢复
        this._activeHubId = undefined;
      }

      this.emit('walletHubChange', undefined, activeWalletHub);
      this.emit('walletChange', this.wallets);
      if (disconnect) {
        activeWalletHub.disconnect();
      }
    }
  }

  private async _init() {
    const { activeWalletHubStorageKey, getInitialWalletHub, getWalletHubs, autoRecoveryAll } = this.option;
    const walletHubs = await getWalletHubs();
    const preAddHubs = this.walletHubs || [];
    // 将提前添加的hub 进行防重（丢弃)
    this.walletHubs = [...walletHubs];
    preAddHubs.forEach((h) => {
      if (this.walletHubs.find((w) => w.id === h.id)) {
        debugLog('重复的wallet hub', h);
        return;
      }
      this.walletHubs.push(h);
    });
    // 尝试直接将wallet hub 的状态进行恢复（其内部会建立钱包的事件监听，保持与插件的同步）
    if (autoRecoveryAll) {
      // 要不要等所有的钱包都连接完才进行下一步？，感觉不需要，反正只是尝试恢复之前连接
      Promise.all(walletHubs.map((w) => w.connect(true)));
    }
    const storedWalletHubName = getCacheObj<string>(activeWalletHubStorageKey);
    const initialWalletHub = getInitialWalletHub ? await getInitialWalletHub(storedWalletHubName) : storedWalletHubName;
    this._initialHubName = initialWalletHub;
    await this._recoveryInitial();
    return;
  }

  private _initialHubName?: string;

  private _triedRecoveryInitial = false;
  private async _recoveryInitial() {
    debugLog('try to recover', this._initialHubName);
    // 没有初始的钱包、或没有对应的钱包连接器（例如卸载了）、或已经有地址（用户已经又连别的了），都不用再进行恢复
    if (this._triedRecoveryInitial || !this._initialHubName || !this.hasWalletHub(this._initialHubName) || this.hasAddress) {
      return;
    }

    this._triedRecoveryInitial = true;

    return this.switch(this._initialHubName, true).catch((err) => {
      console.error(err);
      cacheObj(undefined, this.option.activeWalletHubStorageKey);
    });
  }

  private _listenHub(hub: WalletHub) {
    const unSubWalletChange = addListener(
      hub,
      'walletChange',
      onlyCallOnceInOneTick(() => {
        if (this.isEmpty) {
          this.dispose(false);
        }
        this.emit('walletChange', this.wallets);
      }),
    );
    const dispose = () => {
      unSubWalletChange();
    };
    return dispose;
  }
}
